import useStatefulDebouncedCallback from "app/pages/sales-intelligence/hooks/useStatefulDebouncedCallback";
import {
    compareByPrimaryNameMatch,
    nameMatchesSearchTerm,
    toItemWithMatchedSubStrings,
} from "app/pages/sales-intelligence/sub-modules/advanced-search/helpers/technologies";
import type {
    CategoryListItemType,
    SubCategoryListItemType,
    TechnologiesBaseListItem,
    TechnologyListItemType,
} from "app/pages/sales-intelligence/sub-modules/advanced-search/models/filters/technology/types";
import { useCallback, useMemo, useState } from "react";
import type { SortedTechnologies } from "app/pages/sales-intelligence/modules/technology/types/technologies";
import { translate } from "app/filters/i18nFilter";

type SearchProps = {
    value: string;
    placeholder: string;
    onChange(value: string): void;
    isFocused?: boolean;
};

type HookOptions = {
    shouldProcessItems?: boolean;
};

type HookParams = {
    categories: readonly CategoryListItemType[];
    subCategories: readonly SubCategoryListItemType[];
    technologies: readonly TechnologyListItemType[];
    onItemsSort?<I extends TechnologiesBaseListItem>(items: ReadonlyArray<I>): ReadonlyArray<I>;
    onNoResultsFound?(query: string): void;
    searchPlaceholder?: string;
};

type HookValue = {
    sortedItems: SortedTechnologies;
    searchTerm: string;
    onSearch(query: string): void;
    searchProps: SearchProps;
};

const DEFAULT_OPTIONS: HookOptions = {
    shouldProcessItems: true,
};

const useTechnologiesSearch = (
    { categories, subCategories, technologies, onItemsSort, onNoResultsFound, searchPlaceholder }: HookParams,
    options: HookOptions = DEFAULT_OPTIONS,
): HookValue => {
    const [searchTerm, setSearchTerm] = useState("");

    /**
     * The main transformer function for each array of items.
     * It filters out all unmatched items.
     * Then it maps them to objects that contain matched string.
     * And sorts by matched string priority, meaning that primary string is coming first.
     */
    const processItemsAccordingToSearchTerm = (term: string) => {
        /** SearchTerm filter predicate */
        const matchesSearchTerm = nameMatchesSearchTerm(term);
        /** SearchTerm mapper to make matches bold */
        const toItemWithSubStringMatches = toItemWithMatchedSubStrings(term);

        return <T extends TechnologiesBaseListItem>(items: readonly T[]): readonly T[] =>
            items.filter(matchesSearchTerm).map(toItemWithSubStringMatches).sort(compareByPrimaryNameMatch);
    };

    const sortTabItems = <T extends TechnologiesBaseListItem>(items: readonly T[]): readonly T[] => {
        if (typeof onItemsSort === "function") {
            return onItemsSort(items);
        }

        return items.slice().sort(compareByPrimaryNameMatch);
    };

    const processedItems = useMemo(() => {
        const items = [categories, subCategories, technologies] as unknown as SortedTechnologies;

        if (!options.shouldProcessItems) {return items;}

        const processItemsFn = searchTerm.length > 0 ? processItemsAccordingToSearchTerm(searchTerm) : sortTabItems;

        return items.map(processItemsFn) as unknown as SortedTechnologies;
    }, [searchTerm, categories, subCategories, technologies, options.shouldProcessItems]);

    const handleNoResultsDebounced = useStatefulDebouncedCallback(
        processedItems,
        (query: string, latestSortedItems: SortedTechnologies) => {
            if (query.length === 0) {return;}

            const isNoQueryResultsAvailable = latestSortedItems.every((sortedItems) => sortedItems.length === 0);

            if (isNoQueryResultsAvailable && typeof onNoResultsFound === "function") {
                onNoResultsFound(query);
            }
        },
    );

    const onSearch = useCallback(
        (query: string) => {
            setSearchTerm(query);
            handleNoResultsDebounced(query);
        },
        [handleNoResultsDebounced],
    );

    const searchProps: SearchProps = useMemo(() => {
        const placeholder = searchPlaceholder ?? translate("si.lead_gen_filters.technologies.dd_placeholder");

        return {
            value: searchTerm,
            onChange: onSearch,
            placeholder: placeholder,
        };
    }, [searchTerm, onSearch, searchPlaceholder]);

    return { sortedItems: processedItems, searchTerm, onSearch, searchProps };
};

export default useTechnologiesSearch;
